import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Senha precisa ter no mínimo 6 caracteres");
    if (password !== confirm) return toast.error("As senhas não conferem");
    setLoading(true);
    try {
      const { data } = await api.post("/agency/set-password", { token, new_password: password });
      setAuth(data.token, data.user);
      toast.success("Senha definida! Bem-vindo!");
      if (data.user.role === "agency_admin") navigate("/imobiliaria");
      else navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao definir senha");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#071129] text-white flex items-center justify-center p-5">
        <div className="max-w-md text-center">
          <div className="text-red-400 font-heading text-2xl font-bold mb-3">Link inválido</div>
          <p className="text-white/70 text-sm">Este link precisa de um token de setup. Peça pra sua imobiliária te reenviar a mensagem de boas-vindas pelo WhatsApp.</p>
          <Link to="/auth/login" className="mt-6 inline-block text-[#3B9EFF] font-semibold underline">Ir pra tela de login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071129] text-white flex items-center justify-center p-5 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#3B9EFF]/15 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-[#3B9EFF]/10 rounded-full blur-3xl" />

      <div className="w-full max-w-md relative">
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#3B9EFF]/15 border border-[#3B9EFF]/30 items-center justify-center mb-4">
            <KeyRound className="w-7 h-7 text-[#3B9EFF]" />
          </div>
          <div className="text-xs uppercase tracking-[0.24em] text-[#3B9EFF] font-bold">Ativação de conta</div>
          <h1 className="font-heading text-3xl font-extrabold mt-2">Define sua senha</h1>
          <p className="text-white/60 text-sm mt-3">
            Bem-vindo! Escolha uma senha pra acessar seu painel no <span className="text-white font-semibold">estatebroker<span className="text-[#3B9EFF]">.AI</span></span>.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4 bg-[#0F1F3D] border border-white/10 rounded-2xl p-6">
          <div>
            <Label htmlFor="password" className="text-white/80">Nova senha</Label>
            <Input id="password" data-testid="set-password-input" type="password" required minLength={6}
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" placeholder="Mínimo 6 caracteres" />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-white/80">Confirme a senha</Label>
            <Input id="confirm" data-testid="set-password-confirm" type="password" required minLength={6}
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="!bg-white/10 !text-white !border-white/20 placeholder:!text-white/40" />
          </div>
          <Button type="submit" data-testid="set-password-submit" disabled={loading}
            className="w-full bg-[#3B9EFF] hover:bg-[#1F7CE0] text-white py-6 rounded-full font-semibold">
            {loading ? "Ativando..." : "Ativar conta"} <ArrowRight className="ml-2 w-4 h-4" />
          </Button>

          <div className="pt-4 border-t border-white/10 space-y-2 text-xs text-white/60">
            <div className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-[#3B9EFF] flex-shrink-0 mt-0.5" />
              <span>Depois de ativar, salve <b>{process.env.REACT_APP_WHATSAPP_NUMBER || "+55 48 9949-0057"}</b> na agenda e mande um &quot;oi&quot; pelo WhatsApp.</span>
            </div>
            <div className="flex gap-2 items-start">
              <CheckCircle2 className="w-4 h-4 text-[#3B9EFF] flex-shrink-0 mt-0.5" />
              <span>Eu vou te reconhecer pelo número e começar a trabalhar seus leads.</span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
